import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, Landmark } from 'lucide-react';
import { api } from '@/api/client';

interface BankRow {
  bank_account_name?: string;
  bank_name?: string;
  bank_account_no?: string;
  ifsc_code?: string;
  branch?: string;
  upi_id?: string;
}

export function BankRechargePanel() {
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['settings', 'bank_details_for_recharge'],
    queryFn: async () => {
      try { return (await api.get<BankRow>('/settings/bank_details_for_recharge')).data ?? {}; }
      catch { return {} as BankRow; }
    },
  });

  const { register, handleSubmit, reset, formState: { isDirty } } = useForm<BankRow>({ defaultValues: data ?? {} });
  useEffect(() => { if (data) reset(data); }, [data, reset]);

  const save = useMutation({
    mutationFn: async (d: BankRow) => (await api.put('/settings/bank_details_for_recharge', d)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings', 'bank_details_for_recharge'] }); toast.success('Bank details saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-5 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Bank Details for Wallet Recharge</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Shown to merchants when they initiate a manual NEFT/IMPS recharge. Keep this in sync with your operations account.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Account Holder Name">
          <input {...register('bank_account_name')} placeholder="GAINT LOGISTIC PVT LTD" className="input" />
        </Field>
        <Field label="Bank Name">
          <input {...register('bank_name')} placeholder="ICICI BANK" className="input" />
        </Field>
        <Field label="Account Number">
          <input {...register('bank_account_no')} placeholder="696705600583" className="input font-mono" />
        </Field>
        <Field label="IFSC Code">
          <input {...register('ifsc_code')} placeholder="ICIC0006967" className="input uppercase font-mono" />
        </Field>
        <div className="col-span-2">
          <Field label="Branch">
            <input {...register('branch')} placeholder="Sector-11, Noida-201301" className="input" />
          </Field>
        </div>
        <Field label="UPI ID">
          <input {...register('upi_id')} placeholder="company@upi" className="input" />
        </Field>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
        <button type="button" onClick={() => reset(data ?? {})} disabled={!isDirty}
          className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50">Reset</button>
        <button type="submit" disabled={save.isPending || !isDirty}
          className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2 text-sm">
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Bank Details
        </button>
      </div>

      <div className="text-xs text-slate-400 flex items-center gap-1">
        <Landmark size={12} /> Manual recharge UPI / RTGS instructions will pick these values up automatically.
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
